/**
 * product-upsell.js
 *
 * Handles the Flower Pin & Tutu upsell cards on both:
 *  - Product pages (only rendered for the target collection)
 *  - Cart page (rendered when cart contains an eligible product)
 *
 * Features:
 *  - Colour/variant swatch selection
 *  - Product image swap on swatch hover/select
 *  - AJAX add to cart — separate line items, no interference with
 *    any other app or Zepto personalisation
 *  - "Added" confirmation state, then card hides on cart page
 *
 * No jQuery/Zepto. Vanilla JS only.
 */

(function () {
  'use strict';

  /* ── CART API ───────────────────────────────────────────────────── */
  function cartAdd(payload) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || r.statusText); });
      return r.json();
    });
  }

  function notifyCartUpdate() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  /* ── STATUS MESSAGES ────────────────────────────────────────────── */
  function setStatus(wrap, msg, isError) {
    var el = wrap.querySelector('.upsell-status');
    if (!el) {
      // fallback: look for nearest .upsell-wrap
      var section = wrap.closest('.upsell-wrap');
      if (section) el = section.querySelector('.upsell-status');
    }
    if (!el) return;
    el.textContent = msg;
    el.style.color = isError ? '#c0392b' : '#2e7d32';
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.textContent = ''; }, 5000);
  }

  /* ── BUTTON STATE ───────────────────────────────────────────────── */
  function setBtnState(btn, state) {
    // state: 'idle' | 'loading' | 'done'
    var text    = btn.querySelector('.upsell-card__add-btn-text');
    var spinner = btn.querySelector('.upsell-card__add-btn-loading');
    var done    = btn.querySelector('.upsell-card__add-btn-done');

    btn.disabled = state !== 'idle';
    if (text)    text.hidden    = state !== 'idle';
    if (spinner) spinner.hidden = state !== 'loading';
    if (done)    done.hidden    = state !== 'done';
  }

  /* ── SWATCH SELECTION ───────────────────────────────────────────── */
  function initCard(card) {
    var context  = card.dataset.context;
    var handle   = card.dataset.productHandle;
    var uid      = handle + '-' + context;

    var imgEl          = card.querySelector('.upsell-card__img');
    var priceEl        = card.querySelector('.upsell-card__price');
    var swatchLabel    = card.querySelector('.upsell-card__swatch-selected');
    var addBtn         = card.querySelector('.upsell-card__add-btn');
    var swatches       = card.querySelectorAll('.upsell-swatch__radio');
    var singleVariant  = card.querySelector('.upsell-single-variant');

    // ── Swatch hover: preview image only
    card.querySelectorAll('.upsell-swatch').forEach(function (label) {
      label.addEventListener('mouseenter', function () {
        var imgUrl = label.dataset.variantImage;
        if (imgEl && imgUrl && imgUrl !== 'null') imgEl.src = imgUrl;
      });
      label.addEventListener('mouseleave', function () {
        // Revert to selected swatch image
        var checked = card.querySelector('.upsell-swatch__radio:checked');
        if (checked && imgEl) {
          var checkedImg = checked.closest('.upsell-swatch').dataset.variantImage;
          if (checkedImg && checkedImg !== 'null') imgEl.src = checkedImg;
        }
      });
    });

    // ── Swatch change: commit selection
    swatches.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        var label     = radio.closest('.upsell-swatch');
        var imgUrl    = label ? label.dataset.variantImage : null;
        var price     = parseInt(radio.dataset.variantPrice, 10);
        var title     = radio.dataset.variantTitle;

        if (imgEl && imgUrl && imgUrl !== 'null') imgEl.src = imgUrl;
        if (priceEl && price) priceEl.textContent = '$' + (price / 100).toFixed(2);
        if (swatchLabel) swatchLabel.textContent = title;

        // Re-enable add button if it was in done state
        if (addBtn && !addBtn.disabled) setBtnState(addBtn, 'idle');
      });
    });

    // ── Add to cart
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var variantId;
        var price;

        if (swatches.length > 0) {
          var checked = card.querySelector('.upsell-swatch__radio:checked');
          if (!checked) {
            setStatus(card, 'Please choose a colour first.', true);
            return;
          }
          variantId = parseInt(checked.value, 10);
          price     = parseInt(checked.dataset.variantPrice, 10);
        } else if (singleVariant) {
          variantId = parseInt(singleVariant.value, 10);
          price     = parseInt(singleVariant.dataset.variantPrice, 10);
        }

        if (!variantId) return;

        setBtnState(addBtn, 'loading');

        cartAdd({
          id: variantId,
          quantity: 1,
          properties: {
            '_addon': 'true',
            '_addon_product': handle
          }
        })
        .then(function () {
          setBtnState(addBtn, 'done');
          notifyCartUpdate();

          var productTitle = addBtn.dataset.productTitle || 'Item';
          setStatus(card.closest('.upsell-wrap') || card, '✓ ' + productTitle + ' added!', false);

          // On cart page: slide card out after success
          if (context === 'cart') {
            setTimeout(function () {
              card.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
              card.style.opacity    = '0';
              card.style.transform  = 'translateY(-8px)';
              setTimeout(function () {
                card.hidden = true;
                // If all cards are hidden, hide the whole section
                var wrap  = card.closest('.upsell-wrap--cart');
                if (wrap) {
                  var visible = wrap.querySelectorAll('.upsell-card:not([hidden])');
                  if (visible.length === 0) wrap.hidden = true;
                }
              }, 420);
            }, 1800);
          }
        })
        .catch(function (err) {
          setBtnState(addBtn, 'idle');
          setStatus(card.closest('.upsell-wrap') || card, err.message || 'Could not add item — please try again.', true);
        });
      });
    }
  }

  /* ── INIT ───────────────────────────────────────────────────────── */
  function init() {
document.querySelectorAll('.upsell-card:not([data-context="cart"])').forEach(initCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();